import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ScheduleResult, UpdateScheduleEntryDto } from "@myapp/contract";
import { appointmentKeys } from "../../../appointment/api/appointment.keys";
import { rescheduleKeys } from "../../../reschedule";
import { taskKeys } from "../../../task/api/task.keys";
import { durationsFromInstants } from "../../../../shared/lib/schedule";
import { applyEntryPatch, type EntryPatch } from "../../model/optimisticEntry";
import { scheduleKeys } from "../schedule.keys";
import { scheduleApi } from "../service";

/**
 * Move, resize or re-assign a block — the calendar's write, addressed by schedule-entry id.
 *
 * **Optimistic, and the ordering below is load-bearing.** The drag primitive clears its ghost the
 * moment the pointer is released, so anything the calendar does not already know lands as a flash:
 * the block repainted at its old slot and jumped to the new one when the refetch answered. The patch
 * closes that window.
 *
 * The optimistic state is derived from `dto` rather than from the gesture. `buildSchedulePatch`
 * already decided what the drag means for this entry kind, and its DTO says so in full — reading it
 * back keeps one drag rule instead of two that can disagree.
 *
 * The earlier note here said an optimistic patch was unsafe because `PrismaAbsenceRepo.update` does
 * `deleteMany + create`, so every child `ScheduleEntry.id` changes. That is still true, and it is
 * why nothing here may outlive the request: the `onSettled` invalidation replaces the whole result
 * with the server's, and `onError` restores the snapshot. A multi-technician absence — the case
 * where one id really does stand for several rows — never reaches this hook, because `dragPolicy`
 * makes it inert.
 */
export function useUpdateScheduleEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entryId, dto }: { entryId: number; dto: UpdateScheduleEntryDto }) =>
      scheduleApi.updateEntry(entryId, dto).then((r) => r.data),

    onMutate: async ({ entryId, dto }) => {
      const patch = toEntryPatch(dto);

      const previousQueries = queryClient.getQueriesData<ScheduleResult>({
        queryKey: scheduleKeys.ranges(),
      });

      // Synchronously, before the `await` below. A yield here lets the browser paint once with the
      // old placement, which is the single frame of snap-back this hook exists to remove: applied
      // now, the patch shares a render with the drag primitive's `setDrag(null)`.
      if (patch) {
        queryClient.setQueriesData<ScheduleResult>(
          { queryKey: scheduleKeys.ranges() },
          (old) => (old ? applyEntryPatch(old, { entryId }, patch) : old),
        );
      }

      // After the write: an in-flight refetch that resolves later would otherwise overwrite it.
      await queryClient.cancelQueries({ queryKey: scheduleKeys.ranges() });

      return { previousQueries };
    },

    // A rejected drop — a 409 the user has not confirmed yet — puts the block back where it came
    // from. The dialog that follows then asks about a move the calendar is no longer claiming.
    onError: (_err, _vars, context) => {
      for (const [queryKey, data] of context?.previousQueries ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },

    // The owner caches are invalidated too: the server routes this to the owning use-case, so an
    // appointment or task really did change. `rescheduleKeys` for the same reason — a drop can
    // flag what it displaces (FORCE_AND_FLAG) and moving off a slot lets the sweeper un-flag,
    // so the Umdisponierung list and its nav badge both move with every drag.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: rescheduleKeys.all });
    },
  });
}

/**
 * The DTO as a placement. `null` for a patch that says nothing about *when* — a bare `technicianId`
 * change has no shape to draw, so the calendar waits for the server rather than guessing.
 */
function toEntryPatch(dto: UpdateScheduleEntryDto): EntryPatch | null {
  const technicianId = dto.technicianId ?? undefined;

  if (dto.slot) {
    return dto.slot.kind === "SPAN"
      ? { kind: "SPAN", startsAt: dto.slot.startsAt, endsAt: dto.slot.endsAt, technicianId }
      : {
          kind: "SEGMENTED",
          workStartsAt: dto.slot.workStartsAt,
          travelMinutes: dto.slot.travelMinutes ?? 0,
          workMinutes: dto.slot.workMinutes,
          departureMinutes: dto.slot.departureMinutes ?? 0,
          technicianId,
        };
  }

  // A completed block is drawn from its actuals, which is what `buildSchedulePatch` writes for it —
  // occupancy prefers them per component. The DTO carries the four instants; the minutes the server
  // will derive from them are derived here the same way, so the optimistic frame and the refetch
  // agree. No fallback for an absent outer instant: the board always sends all four.
  if (dto.actualWorkStartsAt != null && dto.actualEndsAt != null) {
    const instants = {
      travelStartedAt: dto.travelStartedAt ?? "",
      actualStart: dto.actualWorkStartsAt,
      actualEnd: dto.actualEndsAt,
      departureEndedAt: dto.departureEndedAt ?? "",
    };
    const durations = durationsFromInstants(instants);
    if (!durations) return null;
    return {
      kind: "SEGMENTED",
      workStartsAt: dto.actualWorkStartsAt,
      travelMinutes: durations.travel,
      workMinutes: durations.work,
      departureMinutes: durations.departure,
      actuals: true,
      instants,
      technicianId,
    };
  }

  return null;
}
